/**
 * Unified product-family picker (sunglasses + basket).
 * Client-side variant switching — no section fetch on click.
 */
(function () {
  'use strict';

  if (window.MPProductFamily?.initialized) return;

  const INITIAL_PRELOAD_DELAY_MS = 1800;
  const catalogById = new Map();
  let saveLabel = 'SAVE';

  const MODE_CONFIG = {
    sunglasses: {
      activeClass: 'mp-sunglasses-family-picker__swatch--active',
      isFamily(productInfo) {
        return Boolean(productInfo.querySelector('[data-mp-product-family="sunglasses"], [data-mp-sunglasses-family]'));
      },
    },
    basket: {
      activeClass: 'mp-family-picker__swatch--active',
      isFamily(productInfo) {
        return Boolean(productInfo.querySelector('[data-mp-product-family="basket"]'));
      },
    },
  };

  window.MPProductFamily = { initialized: true, catalogById, applyVariant };

  function runWhenIdle(callback) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 3000 });
    } else {
      setTimeout(callback, 150);
    }
  }

  function getProductInfo(node) {
    return node?.closest?.('product-info') || null;
  }

  function getFamilyRoot(productInfo) {
    return productInfo.querySelector('[data-mp-product-family], [data-mp-sunglasses-family]');
  }

  function getFamilyMode(productInfo) {
    const root = getFamilyRoot(productInfo);
    if (!root) return null;
    if (root.dataset.mpProductFamily) return root.dataset.mpProductFamily;
    if (root.hasAttribute('data-mp-sunglasses-family')) return 'sunglasses';
    return null;
  }

  function readCatalog(productInfo) {
    const script = productInfo?.querySelector('[data-mp-family-catalog]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  function loadCatalog(productInfo) {
    const data = readCatalog(productInfo);
    if (!data?.variants?.length) return null;

    saveLabel = data.saveLabel || 'SAVE';
    data.variants.forEach(function (variant) {
      catalogById.set(String(variant.id), variant);
    });

    return data;
  }

  function resolveVariantId(productInfo) {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('variant');
    if (fromUrl && catalogById.has(fromUrl)) return fromUrl;

    const catalog = readCatalog(productInfo);
    if (catalog?.activeVariantId && catalogById.has(String(catalog.activeVariantId))) {
      return String(catalog.activeVariantId);
    }

    const input = productInfo.querySelector('input[name="id"]');
    if (input?.value && catalogById.has(input.value)) return input.value;

    return catalog?.variants?.[0]?.id ? String(catalog.variants[0].id) : null;
  }

  function updateVariantInputs(productInfo, variantId) {
    productInfo
      .querySelectorAll(
        `#product-form-${productInfo.dataset.section}, #product-form-installment-${productInfo.dataset.section}`
      )
      .forEach(function (formRoot) {
        const input = formRoot.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variantId;
        input.disabled = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
  }

  function updateSelectedLabel(productInfo, label) {
    const target = productInfo.querySelector('[data-selected-value]');
    if (target) target.textContent = label;
  }

  function updateOptionState(productInfo, variantId, mode) {
    const activeClass = MODE_CONFIG[mode]?.activeClass;
    if (!activeClass) return;

    productInfo.querySelectorAll('[data-mp-family-variant]').forEach(function (option) {
      const isActive = option.dataset.mpFamilyVariant === variantId;
      option.classList.toggle(activeClass, isActive);
      if (option.tagName === 'BUTTON') {
        option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
    });
  }

  function updateSunglassesPriceRow(productInfo, variant) {
    const row = productInfo.querySelector('.mp-sunglasses-price-row');
    if (!row) return;

    const sale = row.querySelector('.mp-sunglasses-sale');
    const compare = row.querySelector('.mp-sunglasses-compare');
    const save = row.querySelector('.mp-sunglasses-save');

    if (sale) sale.textContent = variant.priceFormatted;

    const hasCompare = variant.compareAtPrice > variant.price;
    if (compare) {
      compare.textContent = variant.compareAtFormatted || '';
      compare.hidden = !hasCompare;
    }
    if (save) {
      save.textContent = variant.saveFormatted || '';
      save.hidden = !hasCompare;
    }
  }

  function updateBasketPriceRow(productInfo, variant) {
    const row = productInfo.querySelector('.mp-sale-price-row');
    if (!row) return;

    const sale = row.querySelector('.mp-sale-price-row__sale');
    const compare = row.querySelector('.mp-sale-price-row__compare');
    const save = row.querySelector('.mp-sale-price-row__save');
    const priceRoot = row.querySelector('.price');
    const saleWrap = row.querySelector('.price__sale');
    const regularWrap = row.querySelector('.price__regular');
    const badges = row.querySelector('.price__badges-container');
    const hasCompare = variant.compareAtPrice > variant.price;

    if (sale) sale.textContent = variant.priceFormatted;
    if (compare) compare.textContent = variant.compareAtFormatted || '';
    if (save) save.textContent = variant.saveFormatted || '';

    if (priceRoot) {
      priceRoot.classList.toggle('price--on-sale', hasCompare);
      priceRoot.classList.toggle('price--show-badge', hasCompare);
    }
    if (saleWrap) saleWrap.hidden = !hasCompare;
    if (regularWrap) regularWrap.hidden = hasCompare;
    if (badges) badges.hidden = !hasCompare;
  }

  function updateAvailability(productInfo, variant) {
    const productForm = productInfo.querySelector('product-form');
    const submitButton = productInfo.querySelector('[id^="ProductSubmitButton-"]');
    const submitText = submitButton?.querySelector('span');

    if (productForm && typeof productForm.toggleSubmitButton === 'function') {
      productForm.toggleSubmitButton(
        !variant.available,
        variant.available ? undefined : window.variantStrings?.soldOut
      );
    } else if (submitButton && submitText) {
      if (variant.available) {
        submitButton.removeAttribute('disabled');
        submitText.textContent = window.variantStrings?.addToCart || 'Add to cart';
      } else {
        submitButton.setAttribute('disabled', 'disabled');
        submitText.textContent = window.variantStrings?.soldOut || 'Sold out';
      }
    }

    const variantInput = productInfo.querySelector('input.product-variant-id, input[name="id"]');
    if (variantInput) {
      variantInput.disabled = !variant.available;
    }
  }

  function setSlideImages(slide, imageUrl, alt) {
    if (!slide || !imageUrl) return;

    slide.querySelectorAll('img').forEach(function (img) {
      img.src = imageUrl;
      img.removeAttribute('data-mp-defer-src');
      img.removeAttribute('data-mp-defer-srcset');
      img.srcset = '';
      if (alt) img.alt = alt;
      img.dataset.mpLoaded = 'true';
      img.classList.remove('mp-deferred-media');
    });

    const spinner = slide.querySelector('.loading__spinner');
    if (spinner) spinner.classList.add('hidden');
  }

  function updateFeaturedImage(productInfo, variant) {
    if (!variant.featuredImageUrl) return;

    const activeSlide = productInfo.querySelector('.product__media-item.is-active');
    if (!activeSlide) return;

    setSlideImages(activeSlide, variant.featuredImageUrl, variant.displayLabel);
  }

  function updateBasketHeroImage(productInfo, variant) {
    if (!variant.featuredImageUrl) return;

    const gallery = productInfo.querySelector('media-gallery');
    if (!gallery) return;

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const firstSlide = viewer?.querySelector('.product__media-item');
    if (!firstSlide) return;

    setSlideImages(firstSlide, variant.featuredImageUrl, variant.displayLabel);

    const thumbList = gallery.querySelector('[id^="Slider-Thumbnails"]');
    const firstThumb = thumbList?.querySelector('.thumbnail-list__item');
    if (firstThumb) {
      const thumbImg = firstThumb.querySelector('img');
      if (thumbImg) {
        thumbImg.src = variant.featuredImageUrl;
        thumbImg.removeAttribute('srcset');
        thumbImg.alt = variant.displayLabel || '';
      }
    }

    viewer.querySelectorAll('.product__media-item').forEach(function (slide) {
      slide.classList.remove('is-active');
    });
    firstSlide.classList.add('is-active');

    if (thumbList) {
      thumbList.querySelectorAll('.thumbnail-list__item button').forEach(function (button) {
        button.removeAttribute('aria-current');
      });
      firstThumb?.querySelector('button')?.setAttribute('aria-current', 'true');
    }

    const mediaId = firstSlide.dataset.mediaId;
    if (mediaId && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(mediaId, false);
    } else {
      viewer.querySelector('.product__media-list')?.scrollTo({ left: 0 });
    }
  }

  function updateSunglassesUrl(productInfo, variant) {
    const url = `${variant.productUrl}?variant=${variant.id}`;

    productInfo.dataset.url = variant.productUrl;
    productInfo.dataset.productId = String(variant.productId);

    const variantSelects = getFamilyRoot(productInfo);
    if (variantSelects) {
      variantSelects.dataset.productHandle = variant.productHandle;
      variantSelects.dataset.productId = String(variant.productId);
      variantSelects.dataset.selectedPriceAmount = String((variant.price || 0) / 100);
    }

    const shareButton = productInfo.querySelector('share-button');
    if (shareButton?.updateUrl) {
      shareButton.updateUrl(`${window.shopUrl || ''}${url}`);
    }

    if (productInfo.dataset.updateUrl !== 'false') {
      window.history.replaceState({}, '', url);
    }
  }

  function updateBasketUrl(productInfo, variant) {
    const variantSelects = getFamilyRoot(productInfo);
    if (variantSelects) {
      variantSelects.dataset.selectedPriceAmount = String((variant.price || 0) / 100);
    }

    if (productInfo.dataset.updateUrl === 'false') return;

    const basePath = productInfo.dataset.url || window.location.pathname;
    window.history.replaceState({}, '', `${basePath}?variant=${variant.id}`);
  }

  function updateSelectedVariantJson(productInfo, variant) {
    const script = productInfo.querySelector('[data-selected-variant]');
    if (!script) return;

    script.textContent = JSON.stringify({
      id: variant.id,
      available: variant.available,
      price: variant.price,
      compare_at_price: variant.compareAtPrice,
      featured_media: variant.featuredImageUrl ? { preview_image: { src: variant.featuredImageUrl } } : null,
    });
  }

  function finalizeVariant(productInfo, variant, mode) {
    updateVariantInputs(productInfo, String(variant.id));
    updateAvailability(productInfo, variant);
    updateSelectedVariantJson(productInfo, variant);
  }

  function applyVariant(productInfo, variantId) {
    const variant = catalogById.get(String(variantId));
    const mode = getFamilyMode(productInfo);
    if (!variant || !productInfo || !mode) return;

    updateOptionState(productInfo, String(variant.id), mode);
    updateSelectedLabel(productInfo, variant.displayLabel);

    if (mode === 'sunglasses') {
      updateSunglassesPriceRow(productInfo, variant);
      updateFeaturedImage(productInfo, variant);
      updateSunglassesUrl(productInfo, variant);
      finalizeVariant(productInfo, variant, mode);
      return;
    }

    if (mode === 'basket') {
      updateBasketPriceRow(productInfo, variant);
      updateBasketUrl(productInfo, variant);
      updateBasketHeroImage(productInfo, variant);
      finalizeVariant(productInfo, variant, mode);
      return;
    }

    finalizeVariant(productInfo, variant, mode);
  }

  function preloadFeaturedImages(catalog) {
    if (!catalog?.variants?.length) return;

    catalog.variants.forEach(function (variant) {
      if (!variant.featuredImageUrl) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = variant.featuredImageUrl;
    });
  }

  function schedulePreload(productInfo, catalog) {
    if (!catalog) return;

    if (catalog.family === 'basket') {
      runWhenIdle(function () {
        preloadFeaturedImages(catalog);
      });
      return;
    }

    window.addEventListener(
      'load',
      function () {
        setTimeout(function () {
          runWhenIdle(function () {
            preloadFeaturedImages(catalog);
          });
        }, INITIAL_PRELOAD_DELAY_MS);
      },
      { once: true }
    );
  }

  function initProductInfo(productInfo) {
    const mode = getFamilyMode(productInfo);
    if (!mode || !MODE_CONFIG[mode]?.isFamily(productInfo)) return;
    if (productInfo.dataset.mpProductFamilyBound === 'true') return;

    const catalog = loadCatalog(productInfo);
    if (!catalog) return;

    productInfo.dataset.mpProductFamilyBound = 'true';

    const variantId = resolveVariantId(productInfo);
    if (variantId) applyVariant(productInfo, variantId);

    schedulePreload(productInfo, catalog);
  }

  function handleOptionClick(event) {
    const option = event.target.closest('[data-mp-family-variant]');
    if (!option) return;

    const productInfo = getProductInfo(option);
    if (!getFamilyMode(productInfo)) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = option.dataset.mpFamilyVariant;
    if (!variantId || !catalogById.has(variantId)) return;

    applyVariant(productInfo, variantId);
  }

  document.addEventListener('click', handleOptionClick);

  document.addEventListener('product-info:loaded', function (event) {
    if (event.target?.matches?.('product-info')) {
      initProductInfo(event.target);
    }
  });

  document.querySelectorAll('product-info').forEach(initProductInfo);
})();
